/**
 * Drop tables: what a level-up, a trade-in or a challenge actually hands over.
 *
 * ===========================================================================
 * EVERY ROLL TAKES AN `Rng`
 * ===========================================================================
 *
 * There is no `Math.random` in this file and there must never be one. Rolls
 * take an `Rng` (src/physics/rng.ts) exactly as the simulation does, for the
 * same three reasons: a drop sequence is reproducible from a seed, a test can
 * assert a distribution instead of hoping, and a player cannot reroll a bad
 * drop by reloading the page. A caller that does not have a generator to hand
 * has to think about where its randomness comes from, which is the point.
 *
 * ===========================================================================
 * THE SHAPE OF A ROLL
 * ===========================================================================
 *
 * A drop is three independent decisions, in order:
 *
 *   1. RARITY, from `DROP_WEIGHTS`. Weighted so that a Black Market is one in
 *      two hundred and a Limited never appears at all — Limiteds are event
 *      items and are granted, not dropped.
 *   2. THE ITEM, in two stages: a slot uniformly among the slots that have an
 *      item at that rarity, then an item uniformly within the slot. Uniform
 *      over the whole rarity would be wrong here in a way it is not in the real
 *      game: our `number` slot alone is 81 of the common items, so a flat draw
 *      would make "a common drop" mean "a number" four times out of five.
 *   3. THE ATTRIBUTES. A paintable item rolls painted `PAINT_CHANCE` of the
 *      time and a certifiable one certified `CERT_CHANCE` of the time, both
 *      independent of each other and of the rarity.
 *
 * Titanium White is the rarest thing in the table by construction: it carries
 * a weight of 3 against 10 for each of the other twelve colours, so it is 2.4 %
 * of painted rolls and about 0.6 % of paintable drops.
 */

import type { Rng } from '../physics/rng';
import {
  CERTIFICATIONS,
  PAINT_COLORS,
  RARITY_ORDER,
  type Certification,
  type CosmeticSlot,
  type ItemDef,
  type PaintColor,
  type Rarity,
} from './cosmetics';
import { ITEMS, itemById, nextRarity, rarityRank } from './items';

// ---------------------------------------------------------------------------
// Weights
// ---------------------------------------------------------------------------

/**
 * Rarity weights, summing to 1000 so a weight reads directly as a per-mille
 * chance. The shape follows Rocket League's published crate odds at the top end
 * — Import ~12 %, Exotic ~4 %, Black Market ~1 % of the RARE-and-up mass — with
 * Common and Uncommon added underneath, because our drops come from levelling
 * rather than from a crate that has already filtered them out.
 *
 * `limited` is zero and must stay zero: those items are `exclusive` and are
 * granted by the pass and by events. `droppableItems` filters them out as well,
 * so this is belt and braces.
 */
export const DROP_WEIGHTS: Readonly<Record<Rarity, number>> = Object.freeze({
  common: 350,
  uncommon: 250,
  rare: 200,
  veryRare: 120,
  import: 55,
  exotic: 20,
  blackMarket: 5,
  limited: 0,
});

/** Chance a paintable item drops painted. */
export const PAINT_CHANCE = 0.25;

/** Chance a certifiable item drops certified. */
export const CERT_CHANCE = 0.15;

/**
 * Paint weights. Twelve colours at 10, Titanium White at 3 — see the header.
 * Written as a table rather than "uniform except one" so that changing the
 * relative desirability of a colour is a one-number edit.
 */
export const PAINT_WEIGHTS: Readonly<Record<PaintColor, number>> = Object.freeze({
  crimson: 10,
  skyBlue: 10,
  pink: 10,
  cobalt: 10,
  burntSienna: 10,
  forestGreen: 10,
  purple: 10,
  saffron: 10,
  lime: 10,
  grey: 10,
  black: 10,
  white: 10,
  titaniumWhite: 3,
});

// ---------------------------------------------------------------------------
// Weighted choice
// ---------------------------------------------------------------------------

/**
 * One draw from a weight table. Consumes exactly one number from `rng`
 * regardless of the table, which is what makes a seeded sequence predictable
 * across a change to the weights.
 */
const weightedPick = <T extends string>(
  rng: Rng,
  keys: readonly T[],
  weights: Readonly<Record<T, number>>
): T | null => {
  let total = 0;
  for (const k of keys) total += Math.max(0, weights[k] ?? 0);
  if (total <= 0) return null;
  let roll = rng.next() * total;
  for (const k of keys) {
    roll -= Math.max(0, weights[k] ?? 0);
    if (roll < 0) return k;
  }
  return keys[keys.length - 1] ?? null;
};

// ---------------------------------------------------------------------------
// The droppable pool
// ---------------------------------------------------------------------------

/** Exclusive items are pass and event rewards; they never appear in a drop. */
const DROPPABLE: readonly ItemDef[] = ITEMS.filter(i => i.exclusive !== true);

const POOL: ReadonlyMap<Rarity, ReadonlyMap<CosmeticSlot, readonly ItemDef[]>> = (() => {
  const out = new Map<Rarity, Map<CosmeticSlot, ItemDef[]>>();
  for (const item of DROPPABLE) {
    let bySlot = out.get(item.rarity);
    if (!bySlot) {
      bySlot = new Map();
      out.set(item.rarity, bySlot);
    }
    const list = bySlot.get(item.slot);
    if (list) list.push(item);
    else bySlot.set(item.slot, [item]);
  }
  return out;
})();

/** Every non-exclusive item at a rarity. Empty for a rarity nothing drops at. */
export const droppableItems = (rarity: Rarity): readonly ItemDef[] => {
  const bySlot = POOL.get(rarity);
  if (!bySlot) return [];
  return [...bySlot.values()].flat();
};

/** True when a rarity has anything at all to give. */
export const hasDroppableItems = (rarity: Rarity): boolean => (POOL.get(rarity)?.size ?? 0) > 0;

/**
 * The rarity a roll will actually resolve to.
 *
 * A rarity with an empty pool steps DOWN, never up: stepping up would turn a
 * missing tier into free value, and the only tier that can be empty in practice
 * is `limited`, whose whole point is that it is not obtainable this way.
 */
export const resolvableRarity = (rarity: Rarity): Rarity | null => {
  for (let i = rarityRank(rarity); i >= 0; i--) {
    const r = RARITY_ORDER[i];
    if (r !== undefined && hasDroppableItems(r)) return r;
  }
  return null;
};

// ---------------------------------------------------------------------------
// Rolls
// ---------------------------------------------------------------------------

/** What a roll produced. Not an `InventoryItem` yet — ./inventory mints those. */
export interface DropRoll {
  readonly itemId: string;
  readonly rarity: Rarity;
  readonly paint?: PaintColor;
  readonly certification?: Certification;
}

export interface RollOptions {
  /** Force the rarity instead of drawing one. Used by trade-in. */
  rarity?: Rarity;
  /** Restrict to these slots — the pass's "a wheel this tier" rewards. */
  slots?: readonly CosmeticSlot[];
  /** Override the painted chance, 0..1. */
  paintChance?: number;
  /** Override the certified chance, 0..1. */
  certChance?: number;
}

export const rollRarity = (rng: Rng): Rarity =>
  weightedPick(rng, RARITY_ORDER, DROP_WEIGHTS) ?? 'common';

export const rollPaint = (rng: Rng): PaintColor =>
  weightedPick(rng, PAINT_COLORS, PAINT_WEIGHTS) ?? 'grey';

export const rollCertification = (rng: Rng): Certification =>
  CERTIFICATIONS[rng.nextInt(CERTIFICATIONS.length)] ?? 'striker';

/**
 * One item from the pool at a rarity: a slot first, then an item inside it.
 * Returns null only when the rarity has nothing (or nothing in `slots`).
 */
const pickItem = (
  rng: Rng,
  rarity: Rarity,
  slots?: readonly CosmeticSlot[]
): ItemDef | null => {
  const bySlot = POOL.get(rarity);
  if (!bySlot) return null;
  const available = [...bySlot.keys()].filter(s => slots === undefined || slots.includes(s));
  if (available.length === 0) return null;
  const slot = available[rng.nextInt(available.length)];
  const list = slot === undefined ? undefined : bySlot.get(slot);
  if (!list || list.length === 0) return null;
  return list[rng.nextInt(list.length)] ?? null;
};

/**
 * A whole drop. Returns null when nothing in the catalogue can satisfy the
 * request — a `slots` filter naming a slot with no item at any rarity, say.
 * Callers treat null as "no drop", never as an error: a reward that cannot be
 * granted must not take a match summary down with it.
 */
export const rollDrop = (rng: Rng, opts: RollOptions = {}): DropRoll | null => {
  const requested = opts.rarity ?? rollRarity(rng);
  // Walk down from the requested rarity until a tier can satisfy the slot
  // filter, so a "wheels only" reward at a rarity with no wheels still gives
  // wheels rather than nothing.
  for (let i = rarityRank(requested); i >= 0; i--) {
    const rarity = RARITY_ORDER[i];
    if (rarity === undefined) continue;
    const item = pickItem(rng, rarity, opts.slots);
    if (item === null) continue;
    const paintChance = opts.paintChance ?? PAINT_CHANCE;
    const certChance = opts.certChance ?? CERT_CHANCE;
    const paint = item.paintable && rng.next() < paintChance ? rollPaint(rng) : undefined;
    const certification =
      item.certifiable && rng.next() < certChance ? rollCertification(rng) : undefined;
    return {
      itemId: item.id,
      rarity: item.rarity,
      ...(paint ? { paint } : {}),
      ...(certification ? { certification } : {}),
    };
  }
  return null;
};

/** The roll a trade-in of `rarity` produces: one tier up, same attribute odds. */
export const rollTradeIn = (rng: Rng, rarity: Rarity): DropRoll | null => {
  const up = nextRarity(rarity);
  if (up === null) return null;
  return rollDrop(rng, { rarity: up });
};

/**
 * Rarities a trade-in can consume: those with a next tier that actually drops.
 * Black Market is the top of the obtainable ladder, and Limited is above it but
 * unobtainable, so Black Market cannot be traded up — same as the real game.
 */
export const TRADEABLE_RARITIES: readonly Rarity[] = Object.freeze(
  RARITY_ORDER.filter(r => {
    const up = nextRarity(r);
    return up !== null && hasDroppableItems(up);
  })
);

// ---------------------------------------------------------------------------
// Blueprints
// ---------------------------------------------------------------------------

/**
 * What building a blueprint costs, per rarity, in earned Credits — see the
 * header of ./cosmetics for why there is no other kind.
 *
 * THIS IS THE ONLY CREDIT LADDER IN THE GAME. ./shop derives its prices from
 * `blueprintCost` times its own markup rather than keeping a second table, for
 * the reason its header gives: two ladders drift, and the first time they do a
 * Very Rare costs more than an Import. The numbers are scaled to what a match
 * pays out (`creditsForMatch` in ./profile), so a Black Market blueprint is
 * about forty matches of play rather than about four.
 */
export const BLUEPRINT_BASE_COST: Readonly<Record<Rarity, number>> = Object.freeze({
  common: 60,
  uncommon: 100,
  rare: 200,
  veryRare: 400,
  import: 700,
  exotic: 1100,
  blackMarket: 1800,
  limited: 2400,
});

/** Painted variants cost more, and Titanium White most of all. */
export const PAINTED_COST_MULTIPLIER = 1.5;
export const TITANIUM_WHITE_COST_MULTIPLIER = 2.5;

export const blueprintCost = (def: ItemDef, paint?: PaintColor): number => {
  const base = BLUEPRINT_BASE_COST[def.rarity] ?? BLUEPRINT_BASE_COST.common;
  if (paint === undefined) return base;
  const mult = paint === 'titaniumWhite' ? TITANIUM_WHITE_COST_MULTIPLIER : PAINTED_COST_MULTIPLIER;
  return Math.round(base * mult);
};

export interface BlueprintRoll extends DropRoll {
  readonly cost: number;
}

/**
 * A blueprint roll. Uses the same tables as an item drop and then prices it, so
 * "what you could have got" and "what you must pay for" cannot drift apart.
 * Blueprints are never certified — the real game's are not either.
 */
export const rollBlueprint = (rng: Rng, opts: RollOptions = {}): BlueprintRoll | null => {
  const roll = rollDrop(rng, { ...opts, certChance: 0 });
  if (roll === null) return null;
  const def = itemById(roll.itemId);
  if (def === undefined) return null;
  return { ...roll, cost: blueprintCost(def, roll.paint) };
};
